/**
 * Campaign Mode Configuration — "Rival Trainer Road"
 *
 * Orchestrator for the campaign. Assembles the ten worlds and owns the pure
 * progression/difficulty MATH; the content itself (types, builders, constants,
 * species IDs) lives in ./campaign/builders.ts and each world's nodes live in
 * ./campaign/worldN.ts (narrative text in ./campaign/stories/worldN.ts, merged by
 * campaignStories.ts). Shared by:
 *   • Backend — node validation, rival squad construction, XP rewards
 *   • Frontend — map rendering, story/dialogue, level-cap display
 *
 * STRUCTURE: the campaign is a set of ten WORLDS (sequential story chapters), each
 * a 10-level band, so they tile the full 1→100 range. Those bands are the AUTHORED
 * scale — they set rival evolution stages and XP scaling — but they are NOT the
 * levels a fight runs at: COMBAT_MAX_LEVEL clamps every fighter in every mode, the
 * campaign included, so from World 3 on both sides fight at 20. Progression is LINEAR both
 * WITHIN a world (every node unlocks when the previous node in that world is first
 * cleared) and ACROSS worlds (World N unlocks only once World N-1 is fully
 * cleared — World 1's first node is the only one always available).
 *
 * THE SAGA — "The Last Souls", in two acts of five worlds each (full lore in
 * campaignStories.ts): the SOUL-EATER devours souls by eating the names, friends,
 * and reasons they are made of. Only the LAST SOULS remain — 19 of them, one per
 * species, each waiting at a soul node (world finale or featured mini-boss,
 * marked by CampaignNode.soulSpeciesId). Win its trial and the soul entrusts you
 * a SOUL SHARD (future economy: unlock/improve — see
 * docs/superpowers/specs/campaign-soul-shards.md).
 *   ACT I — THE FIVE WARDENS (Worlds 1–5, levels 1–50): the island's Guardians woke
 *   having lost the one thing that anchored them; the player, the new KEEPER, gives
 *   each back what it lost. Mirror Isle (W5) reveals the Soul-Eater has a
 *   heart that wears your own face — and points the way to its source.
 *     • W1 Nature  — Verdant Forest / Mossheart (Gorly; minis Ray/Kobu; Zap holds the stolen Pefareon shard): grief → hunger.
 *     • W2 Water   — Sunken Bay / Tidebell (Fernail; minis Suimon/Aqua): loneliness → tide.
 *     • W3 Fire    — Cinder Pass / Cinder (Drago): forgot its fire was a gift.
 *     • W4 Thunder — Storm Summit / Rumble (Bireon, the songbird whose song was the thunder): dueling the echo of a lost friend.
 *     • W5 Mystery — Mirror Isle / Glimmer (Zaricat): it wears your face.
 *   ACT II — THE FIRST KEEPER (Worlds 6–10, levels 51–100): the Champion's Road
 *   (W6) reveals the Soul-Eater is the First Keeper himself — forgotten when the
 *   last Keepers died — who flees beyond the island's edge; the player pursues him
 *   into the "unremembered" places to gather the four shards of the FIRST SOUL.
 *     • W6  All     — Champion Road (Sumiya, the First Champion; Zip holds the stolen Talion shard): the reveal; he flees.
 *     • W7  Fire    — Ash Fields / Soot (Hotpaw; Zeck holds the stolen Voltaryx shard): fires that forgot they were saved.
 *     • W8  Thunder — Storm Vault / Boltheart (Toro): a memory begged to be forgotten.
 *     • W9  Water   — Pale Coast / Fade (Yeti; mini Pinky): a world without color.
 *     • W10 All     — First Shore / Firstheart (Trixon; mini Pikoto): name him whole;
 *       the Soul-Eater's hunger ends.
 *
 * DIFFICULTY ARC (fully parametrised — see ./campaign/builders.ts): difficulty
 * escalates PER WORLD. Each world's rivals ramp linearly from a START level up to a
 * FINALE level, and BOTH endpoints climb by WORLD_LEVEL_STEP each world. Rival
 * levels are NOT hand-authored — they are DERIVED at load time from each world's
 * level band (getWorldLevelBand) by stampWorldRivalLevels(), so a balance change is
 * a one-line edit to the band constants. With the defaults (start 1, final 10,
 * step 10): World N (0-indexed) covers levels [10N+1 … 10N+10], World 1 caps at 10
 * and World 10 caps at 100. The player level cap for a world is its FINALE level
 * (CAMPAIGN_CAP_BELOW_FINAL_BOSS is 0) — no artificial under-leveling.
 *
 * **LEVEL IS NO LONGER THE ESCALATION AXIS, and the band above is authored scale
 * rather than fought scale.** COMBAT_MAX_LEVEL clamps every fighter in every mode
 * (see combatLevel / scaleCombatConfigForMode), so only Worlds 1-2 ramp below the
 * ceiling; from World 3 on both sides fight at 20 whatever the band says. What still
 * escalates: OPPONENT COUNT (below), each node's evolution ladder, the rival's card
 * loadout, and the rival STAGE the band derives. Anything player-facing must show
 * combatLevel(), never the authored level — a screen must not name a fight the game
 * will not run.
 *
 * OPPONENT COUNT ramps independently of level, on a fixed per-node schedule
 * (CampaignNode.opponentCount, 1-3): each world opens with 1v1 fights, moves to 2v2
 * mid-way, and reaches 3v3 for its final chapter. The rival ALWAYS fields its full
 * opponentCount; the player may bring anywhere from one pet up to that many, and a
 * smaller squad fights outnumbered (see isValidCampaignSquadSize).
 *
 * A battle is won once: its first clear grants the full XP reward (the one refight, Rival of the
 * Day, pays its own bonus). XP is paid per participating pet. First-clear XP
 * SCALES BY WORLD (stampWorldXpRewards): world files author the World-1 base
 * amounts (25/40/50) and World N pays ×N of them, so late worlds pay late-game
 * XP against the flat 1000-XP level cost.
 */

import { SQUAD_SIZE, COMBAT_MAX_LEVEL, SQUAD_POTIONS_PER_BATTLE } from './pvpConstants.js';
import { EVOLUTION_RANK_MAX, type BattleEvolutionRank } from './battleEvolution.js';
import { SUIKAMON_SPECIES } from './speciesConfig.js';
import { getStageForLevel } from './petHelper.js';
import {
  CAMPAIGN_STORY_INTRO as _CAMPAIGN_STORY_INTRO,
  CAMPAIGN_STORY_INTRO_SLIDES as _CAMPAIGN_STORY_INTRO_SLIDES,
  WORLD_TAGLINES_SHORT as _WORLD_TAGLINES_SHORT,
  type CampaignIntroSlide as _CampaignIntroSlide,
} from './campaignStories.js';

// Shared campaign content primitives (types, difficulty/XP constants, builders,
// species IDs) live in the leaf module ./campaign/builders.ts so the per-world
// content files can import them without an import cycle. The values used by the
// math below are imported; all are re-exported so existing
// `import { … } from './campaignConfig.js'` call sites keep working unchanged.
import {
  WORLD_LEVEL_STEP,
  WORLD_1_START_LEVEL,
  WORLD_1_FINAL_LEVEL,
  BOSS_NODE_LEVEL_BONUS,
  FINALE_BOSS_COMPANION_GAP,
  CAMPAIGN_CAP_BELOW_FINAL_BOSS,
  CAMPAIGN_XP_NORMAL,
  CAMPAIGN_XP_MINI_BOSS,
  CAMPAIGN_XP_ZONE_BOSS,
  CAMPAIGN_XP_WORLD_MULTIPLIER_STEP,
  DAILY_RIVAL_XP_WIN,
  DAILY_RIVAL_XP_LOSS,
  DAILY_RIVAL_LEVEL_CAP,
  GIFT_XP_BASE,
  isNarratedNode,
  type CampaignRivalPet,
  type CampaignNodeKind,
  type CampaignEncounterKind,
  type CampaignNode,
  type CampaignReward,
  type CampaignZone,
  type CampaignWorld,
  type NodeSpec,
} from './campaign/builders.js';
// Rival memory toolkit (identity, dialogue variants, rematch scaling) — pure,
// re-exported here so call sites keep a single campaign import surface.
import {
  rivalIdForName,
  selectIntroLine,
  selectOutcomeLine,
  hasMetRival,
  scaleRivalSquadForRematch,
  type RivalEncounterSummary,
  type NodeDialogue,
} from './campaign/rivalMemory.js';
import { WORLD_1 } from './campaign/world1.js';
import { WORLD_2 } from './campaign/world2.js';
import { WORLD_3 } from './campaign/world3.js';
import { WORLD_4 } from './campaign/world4.js';
import { WORLD_5 } from './campaign/world5.js';
import { WORLD_6 } from './campaign/world6.js';
import { WORLD_7 } from './campaign/world7.js';
import { WORLD_8 } from './campaign/world8.js';
import { WORLD_9 } from './campaign/world9.js';
import { WORLD_10 } from './campaign/world10.js';

export {
  WORLD_LEVEL_STEP,
  WORLD_1_START_LEVEL,
  WORLD_1_FINAL_LEVEL,
  BOSS_NODE_LEVEL_BONUS,
  FINALE_BOSS_COMPANION_GAP,
  CAMPAIGN_CAP_BELOW_FINAL_BOSS,
  CAMPAIGN_XP_NORMAL,
  CAMPAIGN_XP_MINI_BOSS,
  CAMPAIGN_XP_ZONE_BOSS,
  CAMPAIGN_XP_WORLD_MULTIPLIER_STEP,
  DAILY_RIVAL_XP_WIN,
  DAILY_RIVAL_XP_LOSS,
  DAILY_RIVAL_LEVEL_CAP,
  GIFT_XP_BASE,
  isNarratedNode,
  rivalIdForName,
  selectIntroLine,
  selectOutcomeLine,
  hasMetRival,
  scaleRivalSquadForRematch,
};
export type {
  CampaignRivalPet,
  CampaignNodeKind,
  CampaignEncounterKind,
  CampaignNode,
  CampaignReward,
  CampaignZone,
  CampaignWorld,
  NodeSpec,
  RivalEncounterSummary,
  NodeDialogue,
};

/**
 * **WHAT THE SAGA IS CALLED**, as a value rather than a line of prose in a comment.
 *
 * It was only ever written in comments and in the stories themselves, so nothing
 * outside the campaign could cite it and nothing could check a citation. `llms.txt`
 * — the file AI crawlers read to learn what this game is — spent releases telling
 * them the campaign was called "The Forgetting", with world names to match, long
 * after it had been rewritten. Anything describing the campaign to the outside world
 * reads this, and `llmsTxt.test.ts` fails if the generated text stops matching it.
 */
export const CAMPAIGN_SAGA_NAME = 'The Last Souls';

/** Campaign opening text shown on the map page — see campaignStories.ts to edit. */
export const CAMPAIGN_STORY_INTRO = _CAMPAIGN_STORY_INTRO;

/** Paged cinematic intro ("The Last Souls") — see campaignStories.ts to edit. */
export const CAMPAIGN_STORY_INTRO_SLIDES = _CAMPAIGN_STORY_INTRO_SLIDES;
export type CampaignIntroSlide = _CampaignIntroSlide;

/** One-sentence world teasers for compact surfaces — see campaignStories.ts to edit. */
export const WORLD_TAGLINES_SHORT = _WORLD_TAGLINES_SHORT;

// ============================================================================
// Worlds — ten sequential story chapters tiling levels 1→100, ten levels per world.
// All have content and unlock in order as the previous world is cleared —
// content lives in ./campaign/worldN.ts. A world with `locked: true` is PARKED.
// ============================================================================

export const CAMPAIGN_WORLDS: CampaignWorld[] = [
  WORLD_1,
  WORLD_2,
  WORLD_3,
  WORLD_4,
  WORLD_5,
  WORLD_6,
  WORLD_7,
  WORLD_8,
  WORLD_9,
  WORLD_10,
];

/**
 * **THE WORLDS A PLAYER CAN REACH.** A world with `locked: true` is PARKED: its content stays
 * authored, compiles and keeps its structural checks (ids, squads, levels), but the game shows it
 * as Coming Soon and NOTHING scores, critiques or polishes it: not the story gauntlet, not the
 * campaign gauntlet, not a critic. Every tool that measures the campaign reads this list, never
 * `CAMPAIGN_WORLDS`, so a parked world's text cannot fail (or pass) a check for a game nobody
 * plays. Re-enabling a world is one `locked: false` — and then every check sees it again.
 */
export const PLAYABLE_CAMPAIGN_WORLDS: CampaignWorld[] = CAMPAIGN_WORLDS.filter((w) => !w.locked);

// ============================================================================
// Progression helpers (pure — shared by backend validation and frontend map)
// ============================================================================

/** All nodes of one world, in that world's linear progression order. */
export function getWorldNodesInOrder(world: CampaignWorld): CampaignNode[] {
  return world.zones.flatMap((z) => z.nodes);
}

/** The node whose win is a Tale's midpoint (its twist plays): the last of the second-to-last zone (the first, if only two). */
export function getMidpointNodeId(world: CampaignWorld): string | undefined {
  const zone = world.zones[Math.max(0, world.zones.length - 2)];
  return zone?.nodes[zone.nodes.length - 1]?.nodeId;
}

/**
 * The battles a Tale CHAPTER (a story step) may follow: any of the Tale's own battles but the
 * finale, whose win plays the result. The midpoint may have one too: its win plays the twist
 * first, and the story step after it is read from the trail (Book One's chapter 2 goes on after
 * the Gorly crashes through Gran's fence, owner 2026-10-26).
 */
export function getChapterSlotNodeIds(world: CampaignWorld): string[] {
  const nodes = getWorldNodesInOrder(world);
  const finale = nodes[nodes.length - 1]?.nodeId;
  return nodes.map((n) => n.nodeId).filter((id) => id !== finale);
}

/**
 * The campaign WORLD index (0-based) from which in-battle healing pills unlock.
 * Worlds 1–2 are a deliberately pill-free opening arc; clearing World 2 enables
 * pills from World 3 (index 2) onward. Single source of truth for the "pills
 * unlock" gate (see getNodePotionCount / arePillsUnlocked and the frontend
 * milestone celebration).
 */
export const CAMPAIGN_PILLS_UNLOCK_WORLD_INDEX = 2;

/** The 0-based index of a node's world in campaign order, or -1 if unknown. */
export function getCampaignWorldIndexForNode(nodeId: string): number {
  const world = getCampaignWorldForNode(nodeId);
  return world ? CAMPAIGN_WORLDS.indexOf(world) : -1;
}

/**
 * Healing pills each side starts a campaign battle with. TWO gates apply:
 *   1. Pills are a mid-game mechanic — no battle grants them before
 *      CAMPAIGN_PILLS_UNLOCK_WORLD_INDEX (World 3); Worlds 1–2 are pill-free.
 *   2. From World 3 on, normal "trail" rivals still give NO pill — it is reserved
 *      for the fights that matter (mini-boss and boss nodes).
 * Squad Ranked is unaffected (it always uses SQUAD_POTIONS_PER_BATTLE). Single
 * source of truth for the campaign pill rule.
 */
/**
 * **HOW FAR A CAMPAIGN OPPONENT MAY BATTLE-EVOLVE — per node kind.**
 *
 * **EVERY RIVAL THAT FIGHTS CLIMBS THE WHOLE LADDER, exactly as the player does.** The
 * campaign's escalation is carried by LEVELS and squad size, which is where it was always
 * really carried; it is not carried by withholding a core mechanic from the opposition.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * **THIS TABLE USED TO READ 1 / 2 / 3, and the story of why it does not is the point.**
 *
 * The authored escalation was written when a rung was worth **+10% / +20%** — a decoration, so
 * capping it decorated the difficulty curve and nothing more. The reasoning was sound at that
 * size: a trail rival is a stepping stone and should behave the same way every run, so let the
 * dice decide nothing on a node the player was going to win anyway.
 *
 * Then a rung became a **multiple of the bar** ({@link EVOLUTION_RANK_HP_BONUS} — x2 / x3 at the
 * time, x1.5 / x2 now that the whole growth is healed), and the same table stopped being an
 * authored curve and became a difficulty DISCOUNT that scales with the mechanic. The player is
 * never capped; a trail rival held at Evolution 1 was fighting a Suikamon on several times its
 * bar with no answer to it. Measured, at 250 attempts a node:
 *
 * | trail cap | easiest nodes (N1, band <=92%) | steepest step (N4, band <=25pts) |
 * |---|---|---|
 * | 1 (authored) | **98.1%** — a walkover | **+33.2pts** — a cliff |
 * | 2 | 92.6% — on the line | UNSTABLE |
 * | **3 (this)** | **70.9%** | **+10.8pts** |
 *
 * The 70.9% and +10.8pts are the numbers the campaign scored BEFORE the HP ladder moved at all
 * (73.2% / +10.7pts), which is what says this is a restoration rather than a new curve.
 *
 * **What it costs, stated plainly:** `N3` (a boss is the wall of its world) measures the boss's
 * margin over its own trail, and that margin now rests entirely on levels and squad size. It was
 * already thin before any of this — **+6.8pts against a +5.0pt floor** — because the cap had
 * been quietly propping it up. If N3 needs widening it is the BOSS LEVELS that widen it, not
 * this table: a boss that is only a wall because its opponent was forbidden the mechanic is not
 * a wall, it is a handicap match.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * - **`normal`**, **`mini_boss`**, **`zone_boss`** — the whole ladder. A rival that is winning
 *   becomes something the player has to answer, which is the entire point of the mechanic.
 * - **`gift`** — never fights; present so the table is TOTAL and a new node kind cannot silently
 *   default to uncapped. This is why the table survives as a table rather than collapsing into a
 *   constant: it stays the one place a future author can hold a node kind back on purpose, and
 *   `campaignEvolutionCap.test.ts` pins that it must never exceed {@link EVOLUTION_RANK_MAX}.
 *
 * **The PLAYER is never capped by this.** It reads the node, and the node describes the
 * opposition.
 */
export const CAMPAIGN_EVOLUTION_CAP: Record<CampaignNodeKind, BattleEvolutionRank> = {
  normal: EVOLUTION_RANK_MAX,
  mini_boss: EVOLUTION_RANK_MAX,
  zone_boss: EVOLUTION_RANK_MAX,
  gift: 1,
};

/** How far this node's rivals may battle-evolve. The single read for the campaign's escalation. */
export function getNodeEvolutionCap(node: CampaignNode): BattleEvolutionRank {
  return CAMPAIGN_EVOLUTION_CAP[node.kind];
}

/**
 * **WHICH NODES DEAL THEIR RIVAL A FIXED HAND THAT REACHES THE TOP OF THE LADDER — the bosses,
 * and only the bosses.**
 *
 * {@link CAMPAIGN_EVOLUTION_CAP} says how far a rival MAY climb; this says whether it is
 * guaranteed to get there. A cap is permission and permission is not a fight: with the ladder
 * open to every kind, whether a boss actually evolved came down to how many Suikamon cards its
 * pile happened to deal it — and the top rung costs `EVOLUTION_CARDS_PER_STEP * 2` cards out of a
 * pile that carries `SUIKAMON_CARDS_PER_PILE`, so a boss reaching **Max Evo** was a thing that
 * happened to some bosses on some seeds. A boss fight has to be the same fight for everybody, and
 * the transformation is the reason the mechanic exists — so a boss's pile is arranged rather than
 * shuffled: it holds a whole set in its opening hand and the next set immediately behind it
 * (`buildFixedEvolutionPile` in `cardEngine.ts`), which converts on round one and again on round
 * two. Deterministic, and it reads on screen: the chip goes `Evo 2` then `Max Evo` while the
 * player watches, because how many cards an opponent holds has always been public.
 *
 * **It is still the deck doing it.** The cards are dealt, held through the pitch and converted by
 * the same engine path the player's are — nothing grants a rung, and nothing takes the ladder away
 * from either side. What the node authors is the ORDER of a pile, which is the one thing a boss
 * fight is allowed to know in advance.
 *
 * **A trail rival is untouched** (`normal` → false): the trail is where a player learns what
 * holding cards is worth, and a rival that always converts would teach it as a certainty.
 * `gift` never fights and is present so the table stays TOTAL — a new node kind cannot silently
 * default to a rigged pile.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * **A BOSS NODE ARRANGES BOTH PILES, AND THAT IS THE MEASUREMENT TALKING.**
 *
 * The boss's guarantee alone was a wall: the hardest node's win rate fell to **26.1%** against
 * `N1`'s 30% floor, the steepest step between two nodes read **+27.5pts** against `N4`'s 25, and
 * the run half needed six retries at the p90 boss. So the campaign deals the PLAYER's lead the
 * same arrangement on the same nodes (`routes/campaign.ts`), and the numbers came back:
 * **`N1` 40.2% ✓, `N4` +14.4pts ✓**, with a boss still the hardest node in 60% of worlds.
 *
 * **The opportunity is guaranteed; the DECISION is not.** A player dealt three Suikamon cards may
 * pitch them for moves and fight at its printed numbers — the AI never does. That is the same
 * trade the mechanic is built on, and symmetry is the property the whole design rests on: *both
 * fighters climb the same ladder from the same floor by the same rule.* A boss fight is where both
 * of you transform.
 *
 * Trail nodes arrange NOTHING, on either side.
 */
export const CAMPAIGN_FIXED_EVOLUTION_HAND: Record<CampaignNodeKind, boolean> = {
  normal: false,
  mini_boss: true,
  zone_boss: true,
  gift: false,
};

/** Whether this node's rival is dealt the fixed, evolve-to-max hand. */
export function getNodeFixedEvolutionHand(node: CampaignNode): boolean {
  return CAMPAIGN_FIXED_EVOLUTION_HAND[node.kind];
}

export function getNodePotionCount(node: CampaignNode): number {
  if (getCampaignWorldIndexForNode(node.nodeId) < CAMPAIGN_PILLS_UNLOCK_WORLD_INDEX) return 0;
  return node.kind === 'normal' ? 0 : SQUAD_POTIONS_PER_BATTLE;
}

/**
 * Whether a node is a reward-only GIFT step (no battle) rather than a rival
 * fight. Single source of truth so the battle helpers (rival-level stamping,
 * squad validation, the map card, battle counts) can skip it uniformly.
 */
export function isGiftNode(node: CampaignNode): boolean {
  return node.kind === 'gift';
}

/**
 * Whether a node is a "boss" fight — a mini-boss or a zone boss, i.e. any node
 * that is not a normal trail rival. Single source of truth so callers (pill
 * grants, the boss-defeat Telegram announcement, …) never re-derive the rule.
 * Gift nodes are never boss nodes.
 */
export function isBossNode(node: CampaignNode): boolean {
  return node.kind === 'mini_boss' || node.kind === 'zone_boss';
}

/**
 * Whether the player has UNLOCKED in-battle pills: true once the world just
 * before CAMPAIGN_PILLS_UNLOCK_WORLD_INDEX (World 2) is fully cleared. Drives the
 * one-time "pills enabled" milestone celebration on the frontend; the actual
 * per-battle grant is decided by getNodePotionCount.
 */
export function arePillsUnlocked(clearedNodeIds: Set<string>): boolean {
  const gateWorld = CAMPAIGN_WORLDS[CAMPAIGN_PILLS_UNLOCK_WORLD_INDEX - 1];
  return !!gateWorld && isWorldCleared(gateWorld, clearedNodeIds);
}

/**
 * The rival level band [start, final] for the world at the given index in
 * CAMPAIGN_WORLDS. Both endpoints climb by WORLD_LEVEL_STEP per world, so this
 * is the single knob that sets every rival level. World 0 → [WORLD_1_START_LEVEL,
 * WORLD_1_FINAL_LEVEL]; each later world adds the step to both ends.
 */
export function getWorldLevelBand(worldIndex: number): { start: number; final: number } {
  const offset = worldIndex * WORLD_LEVEL_STEP;
  return {
    start: WORLD_1_START_LEVEL + offset,
    final: WORLD_1_FINAL_LEVEL + offset,
  };
}

/**
 * The rival level for the node at position `nodeIndex` of `nodeCount` within a
 * world whose band is [start, final]: a linear ramp from start (first node) to
 * final (last node), rounded. A single-node world sits at `final`.
 */
export function getNodeRivalLevel(
  band: { start: number; final: number },
  nodeIndex: number,
  nodeCount: number
): number {
  const lastIndex = nodeCount - 1;
  if (lastIndex <= 0) return band.final;
  return Math.round(band.start + ((band.final - band.start) * nodeIndex) / lastIndex);
}

/** The finale boss level of a world (max rival level in its last BATTLE node), or 0 if empty. */
export function getWorldFinaleBossLevel(world: CampaignWorld): number {
  const battleNodes = getWorldNodesInOrder(world).filter((n) => !isGiftNode(n));
  if (battleNodes.length === 0) return 0;
  return Math.max(...battleNodes[battleNodes.length - 1].squad.map((p) => p.level));
}

/** The player level cap for a world: its finale boss level − CAMPAIGN_CAP_BELOW_FINAL_BOSS. */
export function getWorldPlayerCap(world: CampaignWorld): number {
  return getWorldFinaleBossLevel(world) - CAMPAIGN_CAP_BELOW_FINAL_BOSS;
}

/**
 * Stamp every rival pet's level (and derived stage) from its world's level band.
 * ONE place owns the difficulty curve; squads only author species/nicknames and
 * node() leaves levels at 0. Within a world the rivals ramp linearly from the
 * band's start to its finale; in the FINALE node the boss (slot 0) sits at the
 * finale level and its companions FINALE_BOSS_COMPANION_GAP below it. Runs once
 * at module load, BEFORE the player-cap stamp (which reads finale levels).
 */
function stampWorldRivalLevels(): void {
  CAMPAIGN_WORLDS.forEach((world, worldIndex) => {
    // Ramp over BATTLE nodes only: reward-only gift steps carry no squad and must
    // not shift the linear rival-level curve, so inserting a gift anywhere in a
    // world leaves every fight's difficulty exactly as it was.
    const battleNodes = getWorldNodesInOrder(world).filter((n) => !isGiftNode(n));
    if (battleNodes.length === 0) return; // locked/empty worlds keep placeholders
    const band = getWorldLevelBand(worldIndex);
    // A Tale whose book is still being written ramps over the battles the finished book will
    // have, so its first chapters play as hard now as they will then, and only the book's real
    // last battle is a finale (`CampaignWorld.plannedBattles`).
    const rampCount = Math.max(battleNodes.length, world.plannedBattles ?? 0);
    const lastIndex = rampCount - 1;
    battleNodes.forEach((node, nodeIndex) => {
      /*
       * **A MID-WORLD BOSS STANDS ABOVE ITS TRAIL** — {@link BOSS_NODE_LEVEL_BONUS}, clamped to
       * the world's finale level so it can never pass the player's own cap (criterion N7).
       *
       * Without it the boss mark was decoration on 32 of the campaign's 42 bosses: every
       * non-finale node was stamped at the same linear point of the ramp whatever its kind, and
       * `campaign-gauntlet` measured a boss as +1.1 points harder than its world's trail
       * against a +5 target (N3).
       */
      const baseLevel = getNodeRivalLevel(band, nodeIndex, rampCount);
      const isFinaleNode = nodeIndex === lastIndex;
      const trailLevel =
        !isFinaleNode && isBossNode(node)
          ? Math.min(band.final, baseLevel + BOSS_NODE_LEVEL_BONUS)
          : baseLevel;
      node.squad.forEach((pet, slot) => {
        const level =
          isFinaleNode && slot > 0
            ? Math.max(1, band.final - FINALE_BOSS_COMPANION_GAP)
            : isFinaleNode
              ? band.final
              : trailLevel;
        pet.level = level;
        pet.stage = getStageForLevel(pet.speciesId, level);
      });
    });
  });
}

/**
 * The first-clear XP multiplier for the world at the given index in
 * CAMPAIGN_WORLDS: ×1 for World 1, growing by CAMPAIGN_XP_WORLD_MULTIPLIER_STEP
 * per world (×10 for World 10 with the default step). Keeps campaign XP in step
 * with the flat XP_PER_LEVEL level cost — late worlds pay late-game XP.
 */
export function getWorldXpMultiplier(worldIndex: number): number {
  return 1 + worldIndex * CAMPAIGN_XP_WORLD_MULTIPLIER_STEP;
}

/**
 * Stamp every node's XP rewards from its world's XP multiplier: world files
 * author the WORLD-1 base amounts (CAMPAIGN_XP_NORMAL / MINI_BOSS / ZONE_BOSS)
 * and this scales them by getWorldXpMultiplier(). A battle is won once (no
 * replays), so there is no repeat reward to derive. ONE place owns the XP curve, same pattern as
 * the level-band stamp above. Runs once at module load.
 */
function stampWorldXpRewards(): void {
  CAMPAIGN_WORLDS.forEach((world, worldIndex) => {
    const multiplier = getWorldXpMultiplier(worldIndex);
    for (const node of getWorldNodesInOrder(world)) {
      node.xpReward = node.xpReward * multiplier;
    }
  });
}

/**
 * Stamp every node's playerLevelCap from its world's finale (cap = finale −
 * CAMPAIGN_CAP_BELOW_FINAL_BOSS). ONE place owns the cap rule; node() only sets a
 * placeholder. Runs once at module load so backend validation, battle creation,
 * and the map UI all agree.
 */
function stampWorldPlayerCaps(): void {
  for (const world of CAMPAIGN_WORLDS) {
    const cap = getWorldPlayerCap(world);
    if (cap <= 0) continue; // locked/empty worlds keep the node() placeholder
    for (const n of getWorldNodesInOrder(world)) {
      n.playerLevelCap = cap;
    }
  }
}

/**
 * Stamp every node's stable rival CHARACTER id from its authored rivalName —
 * the same name is deliberately the same character everywhere (recurrence).
 * ONE place owns rival identity (same load-time-stamp pattern as levels/XP);
 * node() leaves the placeholder ''. Keys the campaign_encounters memory table.
 */
function stampRivalIds(): void {
  for (const world of CAMPAIGN_WORLDS) {
    for (const n of getWorldNodesInOrder(world)) {
      n.rivalId = rivalIdForName(n.rivalName);
    }
  }
}

// Order matters: levels first (player caps derive from finale levels).
stampWorldRivalLevels();
stampWorldPlayerCaps();
stampWorldXpRewards();
stampRivalIds();

/** One recurring rival CHARACTER (derived from the stamped nodes). */
export interface CampaignRival {
  rivalId: string;
  name: string;
  title: string;
  /** Every node this character appears at, in campaign order. */
  nodeIds: string[];
}

/**
 * The rival character registry, derived from the authored worlds after
 * stamping — the world files stay the single source of truth for content.
 */
export const CAMPAIGN_RIVALS: ReadonlyMap<string, CampaignRival> = (() => {
  const rivals = new Map<string, CampaignRival>();
  for (const world of CAMPAIGN_WORLDS) {
    for (const n of getWorldNodesInOrder(world)) {
      // Gift givers are not battle rivals — they have no squad or portrait, so
      // they stay out of the rival registry (and the cast gallery).
      if (isGiftNode(n)) continue;
      const existing = rivals.get(n.rivalId);
      if (existing) {
        existing.nodeIds.push(n.nodeId);
      } else {
        rivals.set(n.rivalId, {
          rivalId: n.rivalId,
          name: n.rivalName,
          title: n.rivalTitle,
          nodeIds: [n.nodeId],
        });
      }
    }
  }
  return rivals;
})();

/**
 * The highest level the campaign AUTHORS — the top of the last unlocked world's
 * band. DERIVED as the highest finale among UNLOCKED worlds (never below
 * COMBAT_MAX_LEVEL), so it recomputes when a world unlocks or a band changes. With
 * all ten worlds unlocked this is 100 (World 10's finale).
 *
 * **IT IS NOT A COMBAT CEILING ANY MORE.** It was passed to `createSquadBattle` as
 * the campaign's `maxLevel` *"so finale bosses aren't clamped to the ranked cap"*,
 * which made the campaign the only mode fighting on an uncapped curve — see
 * {@link COMBAT_MAX_LEVEL} for what that measured. Its only remaining job is
 * VALIDATION: the content check below uses it as the outer bound authored levels
 * and player caps must fall inside. Nothing scales stats from it.
 */
export const CAMPAIGN_MAX_LEVEL: number = Math.max(
  COMBAT_MAX_LEVEL,
  ...PLAYABLE_CAMPAIGN_WORLDS.map(getWorldFinaleBossLevel)
);

/**
 * The same bound over EVERY authored world, parked ones included. The structural content check
 * holds a parked world to its own authored scale, so it still loads unchanged the day it is
 * re-enabled; {@link CAMPAIGN_MAX_LEVEL} stays the bound of the game being played.
 */
export const CAMPAIGN_AUTHORED_MAX_LEVEL: number = Math.max(
  COMBAT_MAX_LEVEL,
  ...CAMPAIGN_WORLDS.map(getWorldFinaleBossLevel)
);

/**
 * All playable BATTLE nodes across unlocked worlds (for totals; NOT one unlock
 * chain). Reward-only gift steps are excluded so battle-count consumers stay
 * correct with no per-caller change: the "X/Y rival battles" progress count, the
 * free-tier boundary (isFreeCampaignNode), the Rival-of-the-Day pool, and the
 * souls gallery. The trail itself renders gifts via getWorldNodesInOrder.
 */
export function getCampaignNodesInOrder(): CampaignNode[] {
  return PLAYABLE_CAMPAIGN_WORLDS
    .flatMap(getWorldNodesInOrder)
    .filter((n) => !isGiftNode(n));
}

/**
 * **THE FIRST CAMPAIGN BATTLE IS RIGGED, THE FIRST TIME ONLY.** A new trainer lands in it straight
 * from the hatch and the opening, and losing the very first fight teaches nothing but "this is
 * hard" (owner, 2026-10-19: "the player always wins this first time"). So the first battle node
 * in play order, while it has never been cleared, fields its rivals at this fraction of their
 * HP and they pick their moves at random. A rematch is a normal fight.
 */
export const FIRST_BATTLE_RIVAL_HP_FRACTION = 0.5;

/** True for the first battle node in play order when this player has never cleared it. */
export function isFirstCampaignBattle(nodeId: string, clearedNodeIds: ReadonlySet<string>): boolean {
  const first = getCampaignNodesInOrder()[0];
  return !!first && first.nodeId === nodeId && !clearedNodeIds.has(nodeId);
}

/**
 * Every playable STEP across unlocked worlds in trail order, INCLUDING reward-only
 * gift steps — unlike getCampaignNodesInOrder (battle-only). This is the order the
 * trail renders and the sequence the linear unlock/"passed" checks walk.
 */
export function getCampaignStepsInOrder(): CampaignNode[] {
  return PLAYABLE_CAMPAIGN_WORLDS.flatMap(getWorldNodesInOrder);
}

/**
 * The campaign's SOUL NODES in campaign order — the 19 battles ("The Last
 * Souls", one per species) whose first clear saves that species' soul and, in
 * fiction, entrusts its Soul Shard. A soul is "saved" exactly when its node is
 * cleared, so collection progress derives from clearedNodeIds with no extra
 * storage. Drives the souls gallery, the map's soul badges, and (later) the
 * shard economy's grant points.
 */
export function getCampaignSoulNodes(): CampaignNode[] {
  return getCampaignNodesInOrder().filter((n) => n.soulSpeciesId !== undefined);
}

/** Find a node by ID across all worlds. */
export function getCampaignNode(nodeId: string): CampaignNode | null {
  for (const world of CAMPAIGN_WORLDS) {
    for (const zone of world.zones) {
      const found = zone.nodes.find((n) => n.nodeId === nodeId);
      if (found) return found;
    }
  }
  return null;
}

/** The world a node belongs to, or null. */
export function getCampaignWorldForNode(nodeId: string): CampaignWorld | null {
  return (
    CAMPAIGN_WORLDS.find((w) =>
      w.zones.some((z) => z.nodes.some((n) => n.nodeId === nodeId))
    ) ?? null
  );
}

/** The zone (chapter) a node belongs to, or null. */
export function getCampaignZoneForNode(nodeId: string): CampaignZone | null {
  for (const world of CAMPAIGN_WORLDS) {
    const zone = world.zones.find((z) => z.nodes.some((n) => n.nodeId === nodeId));
    if (zone) return zone;
  }
  return null;
}

/**
 * True once every BATTLE node in a world has been cleared. Reward-only gift
 * steps are deliberately EXCLUDED from world completion: a gift inserted behind
 * a returning player's frontier stays claimable but is optional, so a player who
 * never opens it must not be stuck at "not cleared" forever with the next world
 * permanently locked. Defeating every rival is the real completion bar; gifts are
 * optional bonuses along the way. (For a frontier player the linear chain still
 * runs through gifts via isNodeUnlocked, so they naturally collect each one on
 * the way to the finale.)
 */
export function isWorldCleared(world: CampaignWorld, clearedNodeIds: Set<string>): boolean {
  const nodes = getWorldNodesInOrder(world).filter((n) => !isGiftNode(n));
  return nodes.length > 0 && nodes.every((n) => clearedNodeIds.has(n.nodeId));
}

/** The playable (content-built) world immediately before this one in campaign
 * order, or null if this is the first playable world. */
export function getPreviousPlayableWorld(world: CampaignWorld): CampaignWorld | null {
  const playable = CAMPAIGN_WORLDS.filter((w) => !w.locked);
  const index = playable.indexOf(world);
  return index > 0 ? playable[index - 1] : null;
}

/** The playable (content-built) world immediately after this one in campaign
 * order, or null if this is the last playable world. Drives the story handoff:
 * finishing a story sends the player straight into the next one. */
export function getNextPlayableWorld(world: CampaignWorld): CampaignWorld | null {
  const playable = CAMPAIGN_WORLDS.filter((w) => !w.locked);
  const index = playable.indexOf(world);
  return index >= 0 && index < playable.length - 1 ? playable[index + 1] : null;
}

/**
 * Sequential unlock rule, ACROSS worlds: a world with content (`!locked`) is
 * playable once every world before it has been fully cleared. The first
 * playable world is always open.
 */
export function isWorldUnlocked(world: CampaignWorld, clearedNodeIds: Set<string>): boolean {
  if (world.locked) return false;
  const previous = getPreviousPlayableWorld(world);
  return previous === null || isWorldCleared(previous, clearedNodeIds);
}

/**
 * Linear unlock rule, PER WORLD: the first node of an unlocked world is
 * available as soon as the world itself unlocks (see isWorldUnlocked); every
 * other node unlocks when the node before it in the SAME world has been
 * cleared. Nodes in locked or not-yet-unlocked worlds are never unlocked.
 */
export function isNodeUnlocked(nodeId: string, clearedNodeIds: Set<string>): boolean {
  const world = getCampaignWorldForNode(nodeId);
  if (!world || !isWorldUnlocked(world, clearedNodeIds)) return false;
  const order = getWorldNodesInOrder(world);
  const index = order.findIndex((n) => n.nodeId === nodeId);
  if (index === -1) return false;
  if (index === 0) return true;
  return clearedNodeIds.has(order[index - 1].nodeId);
}

// ============================================================================
// Squad size — how many pets each side brings
// ============================================================================

/**
 * The SMALLEST squad any campaign node accepts. Every trainer owns at least one
 * Suikamon, so every node is enterable by every trainer.
 */
export const CAMPAIGN_MIN_SQUAD_SIZE = 1;

/**
 * True when `playerSquadSize` is a legal squad for this node: at least one pet,
 * at most the node's authored `opponentCount`.
 *
 * The rival does NOT shrink to match — a node authored for two always sends two.
 * A rival squad that quietly became one whenever the player brought one read as
 * nonsense (the map says 2v2, the rival owns two Suikamon, then only one shows up).
 * A short squad is still allowed, so a trainer who owns a single Suikamon (free
 * adoption grants exactly one) can enter every node rather than hit a wall at the
 * first two-slot one — they just fight it outnumbered.
 */
export function isValidCampaignSquadSize(node: CampaignNode, playerSquadSize: number): boolean {
  return playerSquadSize >= CAMPAIGN_MIN_SQUAD_SIZE && playerSquadSize <= node.opponentCount;
}

// ============================================================================
// Config validation (used by unit tests; returns human-readable problems)
// ============================================================================

/**
 * Free-to-play tier: guests play ALL of Book One (World 1) free, and the sign-up ask is the
 * book's last page — never a wall partway through it (owner, 2026-10-31: the old boundary at
 * the end of chapter 2 asked too early). Any step outside this world needs an account.
 */
export const FREE_TIER_WORLD_ID = 'w1';

function isInFreeTierWorld(nodeId: string): boolean {
  return getCampaignWorldForNode(nodeId)?.worldId === FREE_TIER_WORLD_ID;
}

/**
 * True when a guest (free-tier) player may battle this node: a battle of a playable Tale
 * inside Book One.
 */
export function isFreeCampaignNode(nodeId: string): boolean {
  return getCampaignNodesInOrder().some((n) => n.nodeId === nodeId) && isInFreeTierWorld(nodeId);
}

/**
 * True when a guest (free-tier) player may claim/reach this STEP — the same free boundary as
 * isFreeCampaignNode, but over the FULL step order so it also covers reward-only gift steps
 * (which isFreeCampaignNode's battle-only pool excludes).
 */
export function isFreeCampaignStep(nodeId: string): boolean {
  return getCampaignStepsInOrder().some((n) => n.nodeId === nodeId) && isInFreeTierWorld(nodeId);
}

/**
 * The battle that ENDS Book One — its first win is where a guest is asked to sign up. Null
 * while any of the book's chapters is still a sealed draft: the last battle that plays today is
 * not the end of the book, and the ask waits for the real ending rather than moving onto it.
 */
export function getFreeTierFinaleNodeId(): string | null {
  const world = PLAYABLE_CAMPAIGN_WORLDS.find((w) => w.worldId === FREE_TIER_WORLD_ID);
  if (!world || (world.draftChapters?.length ?? 0) > 0) return null;
  const battles = getWorldNodesInOrder(world).filter((n) => !isGiftNode(n));
  return battles[battles.length - 1]?.nodeId ?? null;
}

/**
 * Validate the campaign content config. Returns an empty array when valid.
 * Checks: unique node IDs, squad size, valid species with combat configs,
 * level bounds, stage bounds, per-node level cap consistency, and the soul-node
 * rules (every world finale is a soul node; each species' soul appears at most
 * once campaign-wide).
 */
export function validateCampaignConfig(): string[] {
  const problems: string[] = [];
  const seenNodeIds = new Set<string>();
  const seenSoulSpecies = new Map<number, string>();
  for (const world of CAMPAIGN_WORLDS) {
    // Soul rule: the world finale (last node) always carries a species' soul —
    // and can never be a reward-only gift (getWorldFinaleBossLevel reads its squad).
    const worldNodes = getWorldNodesInOrder(world);
    const finale = worldNodes[worldNodes.length - 1];
    if (finale && isGiftNode(finale)) {
      problems.push(`${finale.nodeId}: world finale of ${world.worldId} cannot be a gift node`);
    } else if (finale && finale.soulSpeciesId === undefined && !world.draftChapters?.length) {
      // A book still being written has not reached its real finale (`draftChapters`): its last
      // playable battle is not the Tale's soul trial, so the rule waits for the whole book.
      problems.push(`${finale.nodeId}: world finale of ${world.worldId} has no soulSpeciesId`);
    }
    // Per-world player cap rule: every node caps at the world finale boss level.
    const worldCap = getWorldPlayerCap(world);
    for (const n of getWorldNodesInOrder(world)) {
      if (seenNodeIds.has(n.nodeId)) problems.push(`Duplicate nodeId ${n.nodeId}`);
      seenNodeIds.add(n.nodeId);
      if (!n.rivalId) {
        problems.push(`${n.nodeId}: rivalId not stamped (rivalName '${n.rivalName}')`);
      }
      // Gift nodes are reward-only: no squad/opponent/soul rules apply — they carry
      // a reward bundle instead. Validate the reward, then skip the battle checks.
      if (isGiftNode(n)) {
        const reward = n.reward;
        const empty =
          !reward ||
          (reward.suika === undefined &&
            reward.xp === undefined &&
            reward.tracks === undefined);
        if (empty) {
          problems.push(`${n.nodeId}: gift node has no reward`);
        }
        if (reward?.suika !== undefined && reward.suika <= 0) {
          problems.push(`${n.nodeId}: gift suika must be > 0`);
        }
        if (reward?.xp !== undefined && reward.xp <= 0) {
          problems.push(`${n.nodeId}: gift xp must be > 0`);
        }
        for (const [key, grant] of [
          ['tracks', reward?.tracks],
        ] as const) {
          if (!grant) continue;
          if (!SUIKAMON_SPECIES.find((s) => s.id === grant.speciesId)) {
            problems.push(`${n.nodeId}: gift ${key}.speciesId ${grant.speciesId} is not a known species`);
          }
          if (grant.amount <= 0) {
            problems.push(`${n.nodeId}: gift ${key}.amount must be > 0`);
          }
        }
        continue;
      }
      if (n.squad.length !== SQUAD_SIZE) {
        problems.push(`${n.nodeId}: squad has ${n.squad.length} pets, expected ${SQUAD_SIZE}`);
      }
      if (n.opponentCount < 1 || n.opponentCount > SQUAD_SIZE) {
        problems.push(`${n.nodeId}: opponentCount ${n.opponentCount} outside [1, ${SQUAD_SIZE}]`);
      }
      for (const pet of n.squad) {
        const species = SUIKAMON_SPECIES.find((s) => s.id === pet.speciesId);
        if (!species?.combat) {
          problems.push(`${n.nodeId}: species ${pet.speciesId} missing or has no combat config`);
          continue;
        }
        if (pet.level < 1 || pet.level > CAMPAIGN_AUTHORED_MAX_LEVEL) {
          problems.push(`${n.nodeId}: ${species.name} level ${pet.level} outside [1, ${CAMPAIGN_AUTHORED_MAX_LEVEL}]`);
        }
        if (pet.stage < 1 || pet.stage > species.behavior.maxStage) {
          problems.push(`${n.nodeId}: ${species.name} stage ${pet.stage} outside [1, ${species.behavior.maxStage}]`);
        }
      }
      if (n.playerLevelCap !== worldCap) {
        problems.push(`${n.nodeId}: playerLevelCap ${n.playerLevelCap} should equal world cap ${worldCap} (finale − ${CAMPAIGN_CAP_BELOW_FINAL_BOSS})`);
      }
      if (n.playerLevelCap < 1 || n.playerLevelCap > CAMPAIGN_AUTHORED_MAX_LEVEL) {
        problems.push(`${n.nodeId}: playerLevelCap ${n.playerLevelCap} outside [1, ${CAMPAIGN_AUTHORED_MAX_LEVEL}]`);
      }
      if (n.soulSpeciesId !== undefined) {
        const soulSpecies = SUIKAMON_SPECIES.find((s) => s.id === n.soulSpeciesId);
        if (!soulSpecies) {
          problems.push(`${n.nodeId}: soulSpeciesId ${n.soulSpeciesId} is not a known species`);
        } else if (seenSoulSpecies.has(n.soulSpeciesId)) {
          problems.push(
            `${n.nodeId}: ${soulSpecies.name}'s soul is already featured at ${seenSoulSpecies.get(n.soulSpeciesId)} — each species' soul appears once`
          );
        } else {
          seenSoulSpecies.set(n.soulSpeciesId, n.nodeId);
        }
        if (n.kind === 'normal') {
          problems.push(`${n.nodeId}: soul nodes must be mini_boss or zone_boss, not normal`);
        }
      }
    }
  }
  return problems;
}
